import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { UNITY_ADDRESSABLE_PLATFORMS } from './config';
import { formatBytes, formatInteger, formatRelativeTime } from './utils';

const VoiceSamples = ({
  voiceSampleForm,
  voiceOptionsForProvider,
  voiceSamplesOverview,
  selectedVoiceSample,
  voiceSampleActionState,
  onProviderChange,
  onVoiceChange,
  onFileChange,
  onUpload,
  onDelete,
  onRefresh,
  loading,
}) => (
  <Paper elevation={0} sx={{ p: 3 }}>
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6">Case editor voice samples</Typography>
        <Typography variant="body2" color="text.secondary">
          Upload static sample clips used by the case editor Play sample buttons.
          {' Files are stored under `uploads/voice-samples/<provider>/<voice>.mp3|wav`.'}
        </Typography>
      </Box>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <TextField select label="Provider" value={voiceSampleForm.provider} onChange={onProviderChange} sx={{ minWidth: 200 }}>
          <MenuItem value="openai">OpenAI</MenuItem>
          <MenuItem value="gemini">Gemini</MenuItem>
        </TextField>
        <TextField select label="Voice" value={voiceSampleForm.voice} onChange={onVoiceChange} sx={{ minWidth: 260 }}>
          {voiceOptionsForProvider.length === 0 ? (
            <MenuItem value=""><em>No voices available</em></MenuItem>
          ) : (
            voiceOptionsForProvider.map((voice) => <MenuItem key={voice} value={voice}>{voice}</MenuItem>)
          )}
        </TextField>
      </Stack>

      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          Samples uploaded: {formatInteger(voiceSamplesOverview?.counts?.total || 0)} total
          {' '}({formatInteger(voiceSamplesOverview?.counts?.openai || 0)} OpenAI, {formatInteger(voiceSamplesOverview?.counts?.gemini || 0)} Gemini)
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {selectedVoiceSample?.hasSample
            ? `Current file: ${selectedVoiceSample.fileName} • ${formatBytes(selectedVoiceSample.sizeBytes)} • updated ${formatRelativeTime(selectedVoiceSample.updatedAt, 'just now')}`
            : 'Current file: not uploaded for this voice'}
        </Typography>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
        <Button variant="outlined" component="label">
          {voiceSampleForm.file ? 'Replace audio' : 'Choose audio'}
          <input hidden type="file" accept=".mp3,.wav,audio/mpeg,audio/wav" onChange={onFileChange} />
        </Button>
        <Typography variant="body2" color="text.secondary">{voiceSampleForm.file?.name || 'No file selected'}</Typography>
        {selectedVoiceSample?.hasSample && selectedVoiceSample?.mediaUrl && (
          <Button variant="text" size="small" component="a" href={selectedVoiceSample.mediaUrl} target="_blank" rel="noopener noreferrer">
            Open current sample
          </Button>
        )}
      </Stack>

      {voiceSampleActionState.error && <Alert severity="error">{voiceSampleActionState.error}</Alert>}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <Button variant="contained" onClick={onUpload} disabled={!voiceSampleForm.file || voiceSampleActionState.uploading}>
          {voiceSampleActionState.uploading ? 'Uploading...' : 'Upload sample'}
        </Button>
        <Button variant="outlined" color="error" onClick={onDelete} disabled={!selectedVoiceSample?.hasSample || voiceSampleActionState.deleting}>
          {voiceSampleActionState.deleting ? 'Removing...' : 'Remove sample'}
        </Button>
        <Button variant="outlined" onClick={onRefresh} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </Button>
      </Stack>
    </Stack>
  </Paper>
);

const WebPlayerSettings = ({ webPlayer, webAddressables, saving, onToggleNavVisible }) => (
  <Paper elevation={0} sx={{ p: 3 }}>
    <Stack spacing={2}>
      <Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6">Web player (WebGPU)</Typography>
          <Chip size="small" color="warning" label="Testing" />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Build it from Unity with Virtual Medic &gt; Build &gt; Web (WebGPU). Uploads arrive as drafts in the build list;
          publish one there, and activate the Web Addressables below. Global admins can always open the page, including drafts.
        </Typography>
      </Box>
      {webAddressables?.version ? (
        <Alert severity="success">
          Patients load from Web Addressables {webAddressables.version}
          {webAddressables.buildNumber ? ` (${webAddressables.buildNumber})` : ''}.
        </Alert>
      ) : (
        <Alert severity="warning">
          No Web (WebGPU) Addressables build is active, so patients will fail to load in the browser.
          Activate one under Unity addressables below.
        </Alert>
      )}
      <FormControlLabel
        control={(
          <Switch
            checked={Boolean(webPlayer?.navVisible)}
            disabled={saving}
            onChange={(event) => onToggleNavVisible(event.target.checked)}
          />
        )}
        label="Show Play in browser (Beta) in the navbar for signed-in users"
      />
      {webPlayer?.updatedAt && (
        <Typography variant="caption" color="text.secondary">
          Last changed {formatRelativeTime(webPlayer.updatedAt, 'just now')}
        </Typography>
      )}
      <Box>
        <Button variant="outlined" component={RouterLink} to="/play">Open web player</Button>
      </Box>
    </Stack>
  </Paper>
);

const RuntimeAssetsTab = ({
  addressablesByPlatform,
  addressablesForm,
  addressablesUploadState,
  addressableBuildsByPlatform,
  addressablesOverview,
  addressablesBuildActionState,
  addressablesEditState,
  onAddressablesFieldChange,
  onUploadAddressables,
  onAddressablesBuildEditFieldChange,
  onSaveAddressablesBuild,
  onActivateAddressablesBuild,
  onDeleteAddressablesBuild,
  webPlayer,
  savingWebPlayer,
  onToggleWebPlayerNav,
  voiceSampleProps,
}) => (
  <Stack spacing={3}>
    <Alert severity="info">
      Manage deployable Unity content and case editor media without mixing release assets into billing controls.
    </Alert>

    <WebPlayerSettings
      webPlayer={webPlayer}
      webAddressables={addressablesByPlatform?.web}
      saving={savingWebPlayer}
      onToggleNavVisible={onToggleWebPlayerNav}
    />

    <Paper elevation={0} sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6">Unity addressables</Typography>
          <Typography variant="body2" color="text.secondary">
            Upload versioned addressables zips to Google Cloud Storage, then decide which build becomes the live <code>current</code> URL for each platform.
          </Typography>
        </Box>
        <Grid container spacing={2}>
          {UNITY_ADDRESSABLE_PLATFORMS.map((platform) => {
            const current = addressablesByPlatform[platform.key];
            const formState = addressablesForm[platform.key] || {};
            const uploadState = addressablesUploadState[platform.key] || {};
            const platformBuilds = addressableBuildsByPlatform[platform.key] || [];
            const currentUrl = current?.baseUrl || addressablesOverview?.config?.currentBaseUrls?.[platform.key] || '';
            const fileCount = Number.isFinite(Number(current?.fileCount)) ? Number(current.fileCount) : 0;
            const totalBytes = Number.isFinite(Number(current?.totalBytes)) ? Number(current.totalBytes) : 0;

            return (
              <Grid item xs={12} md={6} key={platform.key}>
                <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                  <Stack spacing={1.5}>
                    <Typography variant="subtitle1">{platform.label}</Typography>
                    <TextField label="Unity RemoteLoadPath URL" value={currentUrl} placeholder="No current URL yet" InputProps={{ readOnly: true }} fullWidth />
                    <Stack spacing={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        Use this exact URL in Unity for this platform. It stays stable while you switch active builds in Global Admin.
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {current?.version
                          ? `Active build: ${current.version}${current?.buildNumber ? ` (${current.buildNumber})` : ''}`
                          : 'Active build: not set'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {current?.uploadedAt ? `Active build uploaded: ${formatRelativeTime(current.uploadedAt)}` : 'Active build uploaded: not yet'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {`${formatInteger(fileCount)} files • ${formatBytes(totalBytes)}`}
                      </Typography>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                      <Button variant="outlined" component="label">
                        {formState.file ? 'Replace .zip' : 'Choose .zip'}
                        <input hidden type="file" accept=".zip" onChange={onAddressablesFieldChange(platform.key, 'file')} />
                      </Button>
                      <Typography variant="body2" color="text.secondary">{formState.file?.name || 'No file selected'}</Typography>
                    </Stack>

                    <TextField label="Version" value={formState.version || ''} onChange={onAddressablesFieldChange(platform.key, 'version')} fullWidth />
                    <TextField label="Build number (optional)" value={formState.buildNumber || ''} onChange={onAddressablesFieldChange(platform.key, 'buildNumber')} fullWidth />

                    {uploadState.error && <Alert severity="error">{uploadState.error}</Alert>}

                    <Button variant="contained" onClick={() => onUploadAddressables(platform.key)} disabled={!formState.file || uploadState.uploading}>
                      {uploadState.uploading ? 'Uploading...' : 'Upload draft build'}
                    </Button>

                    <Divider />

                    <Stack spacing={1}>
                      <Typography variant="subtitle2">Uploaded builds</Typography>
                      {platformBuilds.length === 0 && (
                        <Typography variant="body2" color="text.secondary">No builds uploaded for this platform yet.</Typography>
                      )}
                      {platformBuilds.map((build) => {
                        const rowState = addressablesBuildActionState[build._id] || {};
                        const editState = addressablesEditState[build._id] || {
                          version: build.version || '',
                          buildNumber: build.buildNumber || '',
                        };

                        return (
                          <Paper key={build._id} variant="outlined" sx={{ p: 1.5, borderColor: build.isActive ? 'success.main' : 'divider' }}>
                            <Stack spacing={1}>
                              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                  <Chip
                                    size="small"
                                    color={build.isActive ? 'success' : (build.status === 'draft' ? 'warning' : 'default')}
                                    label={build.isActive ? 'Active' : build.status}
                                  />
                                  {build.buildNumber && <Chip size="small" variant="outlined" label={`Build ${build.buildNumber}`} />}
                                </Stack>
                                <Typography variant="caption" color="text.secondary">Uploaded {formatRelativeTime(build.createdAt)}</Typography>
                              </Stack>

                              <TextField label="Version" size="small" value={editState.version || ''} onChange={onAddressablesBuildEditFieldChange(build._id, 'version')} fullWidth />
                              <TextField label="Build number" size="small" value={editState.buildNumber || ''} onChange={onAddressablesBuildEditFieldChange(build._id, 'buildNumber')} fullWidth />

                              <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary">Version URL: {build.versionBaseUrl}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Files: {formatInteger(build.fileCount || 0)} • Size: {formatBytes(build.totalBytes || 0)}
                                </Typography>
                              </Stack>

                              {rowState.error && <Alert severity="error">{rowState.error}</Alert>}

                              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                <Button variant="outlined" size="small" onClick={() => onSaveAddressablesBuild(build)} disabled={rowState.saving || rowState.activating || rowState.deleting}>
                                  {rowState.saving ? 'Saving...' : 'Save'}
                                </Button>
                                <Button variant="contained" size="small" onClick={() => onActivateAddressablesBuild(build)} disabled={Boolean(build.isActive) || rowState.saving || rowState.activating || rowState.deleting}>
                                  {rowState.activating ? 'Activating...' : 'Set active'}
                                </Button>
                                {build.sourceZipUrl && (
                                  <Button variant="outlined" size="small" component="a" href={build.sourceZipUrl} target="_blank" rel="noreferrer">Download zip</Button>
                                )}
                                <Button variant="outlined" size="small" component="a" href={build.versionBaseUrl} target="_blank" rel="noreferrer">Open URL</Button>
                                <Button variant="text" color="error" size="small" onClick={() => onDeleteAddressablesBuild(build)} disabled={Boolean(build.isActive) || rowState.saving || rowState.activating || rowState.deleting}>
                                  {rowState.deleting ? 'Deleting...' : 'Delete'}
                                </Button>
                              </Stack>
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Stack>
    </Paper>

    <VoiceSamples {...voiceSampleProps} />
  </Stack>
);

export default RuntimeAssetsTab;
